import React from 'react';
import {
  get,
  reduce,
  isEqual,
  keys,
  map,
  findIndex,
  startCase,
  filter,
  has,
  isEmpty,
  find,
  sortBy,
  isNumber,
  max,
} from 'lodash';
import classNames from 'classnames';
import { CONTEXT, publicUrl } from '../config';
import { constants } from '../constants/configuration';

import {
  customizationOps,
  menuCategoryOps,
  menuCategoryTypes,
  mediaOps,
  paymentTypes,
  orderStatusCode,
  submitFallbackText,
  menuItemDetailsPopupFallbackTitle,
  deliveryChargeRuleTypes,
  fallbackTime,
  serviceModes,
  browserMediaOps,
  UIElements,
  emailTypes,
  notificationPlaybackModes,
  itemOps,
  transactionSubStatusConfig,
} from '../constants/globals';
import restaurantRoutes from '../routes/restaurant';
import asyncRoute from '../helpers/hoc/asyncRoute';
import { formatTimeTo12H, formatTime } from '../utils/fudrLocalizationHelper';
import { generateUUID, toTitleCase } from '../utils/helpers';
import { refundTypesConfig, refundStatus } from '../config';
import { featuresStatus } from '../containers/restaurant/config';
import { errorPageConfig } from '../resources';
import { resources } from '../containers/restaurant/resources';

import vegetarianIcon from '../assets/images/icons/vegetarian.svg';
import nonVegetarianIcon from '../assets/images/icons/non-vegetarian.svg';
import eggetarianIcon from '../assets/images/icons/eggetarian.svg';
import defaultFoodIcon from '../assets/images/icons/default-food-type.svg';
import playIcon from '../assets/images/icons/play.svg';
import pauseIcon from '../assets/images/icons/pause.svg';
import stopIcon from '../assets/images/icons/stop.svg';
import fudrPaidIcon from '../assets/images/icons/fudr-paid.svg';
import payToRestaurantIcon from '../assets/images/icons/pay-to-restaurant.svg';
import paidByCashCardIcon from '../assets/images/icons/paid-by-cash-card.svg';
import cashCardPlusPayToRestaurantIcon from '../assets/images/icons/cashCardPlusPayToRestaurant.svg';
import cashCardPlusFudrPaid from '../assets/images/icons/cashCardPlusFudrPaid.svg';
import whiteEditIcon from '../assets/images/icons/edit-white.svg';
import plusIcon from '../assets/images/icons/plus.svg';

const CheckBox = asyncRoute(() => import('../components/elements/CheckBox'));
const Label = asyncRoute(() => import('../components/elements/Label'));

const readyStateTableTypes = constants.readyStateTableTypes;
const orderTransactionDetails = resources.orderTransactionDetails;
const cashCardResources = resources.cashCard;
const integrationsResources = resources.integrations;

export const getItemsCount = (items) =>
  reduce(
    items,
    function (sum, item) {
      return sum + parseInt(get(item, 'quantity', 0));
    },
    0,
  );

export const renderItemCount = ({ items, cancelledItems }) => {
  let label = '';
  if (items && items.length > 0) {
    label = (
      <Label
        classNames="order-count"
        text={`${getItemsCount(items)} ${
          getItemsCount(items) <= 1 ? `${constants.card.item}` : `${constants.card.items}`
        }`}
      />
    );
  } else {
    label = (
      <Label
        classNames="order-count"
        text={`${getItemsCount(cancelledItems)} ${
          getItemsCount(cancelledItems) <= 1 ? `${constants.card.item}` : `${constants.card.items}`
        }`}
      />
    );
  }
  return label;
};

export const renderOrderStatus = ({ statusCode, tableType }) => {
  let orderStatus = '';
  if (readyStateTableTypes.includes(tableType) && isEqual(statusCode, 'CONFIRMED')) {
    switch (tableType) {
      case 'SP':
      case 'DT':
        orderStatus = 'Not Ready';
        break;
      case 'DL':
        orderStatus = 'Not Ready';
        break;
      default:
    }
  } else {
    switch (statusCode) {
      case 'UNATTENDED':
        orderStatus = 'Unattended';
        break;
      case 'CONFIRMED':
      case 'READY':
        switch (tableType) {
          case 'SP':
            orderStatus = 'Unsettled';
            break;
          case 'DL':
            orderStatus = 'Out for Delivery';
            break;
          default:
            orderStatus = 'Unsettled';
        }
        break;
      case 'CANCELLED':
        orderStatus = 'Cancelled';
        break;
      case 'SETTLED':
        orderStatus = 'Delivered';
        break;
      case 'FAILED':
        orderStatus = 'Failed';
        break;
      default:
    }
  }
  return orderStatus;
};

export const renderButtonText = ({ statusCode, tableType }) => {
  let buttontext = '';
  if (readyStateTableTypes.includes(tableType) && isEqual(statusCode, 'CONFIRMED')) {
    switch (tableType) {
      case 'SP':
      case 'DT':
        buttontext = 'MARK READY';
        break;
      case 'DL':
        buttontext = 'START DELIVERY';
        break;
      default:
    }
  } else {
    switch (statusCode) {
      case 'UNATTENDED':
        buttontext = 'ATTEND';
        break;
      case 'CONFIRMED':
      case 'READY':
        switch (tableType) {
          case 'SP':
            buttontext = 'SETTLE';
            break;
          case 'DL':
            buttontext = 'MARK DELIVERED';
            break;
          default:
            buttontext = 'SETTLE';
        }
        break;
      default:
    }
  }
  return buttontext;
};

export const createDropdownOptions = (quantity) => {
  let dropdownOptions = [];
  for (let i = 0; i <= quantity; i++) {
    dropdownOptions.push({ key: i, value: i, text: i });
  }
  return dropdownOptions;
};

export const renderOperatorDropdownOptions = (operatorArr) =>
  map(operatorArr, (operator) => ({
    key: get(operator, 'code', ''),
    value: get(operator, 'code', ''),
    text: get(operator, 'title', ''),
  }));

export const orderState = ({ statusCode, tableType }) => {
  let className = '';
  if (readyStateTableTypes.includes(tableType) && isEqual(statusCode, 'CONFIRMED')) {
    className = 'ready';
  } else {
    className = classNames({
      unattended: statusCode === 'UNATTENDED',
      unsettled: statusCode === 'CONFIRMED',
      delivered: statusCode === 'SETTLED',
      failed: statusCode === 'FAILED',
      cancelled: statusCode === 'CANCELLED',
      'unsettled-ready': statusCode === 'READY',
    });
  }
  return className;
};

export const orderStatusState = ({ statusCode, tableType }) => {
  let className = '';
  if (readyStateTableTypes.includes(tableType) && isEqual(statusCode, 'CONFIRMED')) {
    className = 'status_Ready';
  } else {
    className = classNames({
      status_Unattended: statusCode === 'UNATTENDED',
      status_Unsettled: statusCode === 'CONFIRMED',
      status_Delivered: statusCode === 'SETTLED',
      status_Failed: statusCode === 'FAILED',
      status_Cancelled: statusCode === 'CANCELLED',
      'status_Unsettled-Ready': statusCode === 'READY',
    });
  }
  return className;
};

export const renderReadyPopupContent = ({ tableType }) => {
  switch (tableType) {
    case 'SP':
    case 'DT':
      return constants.orders.readyOrderPopup.selfPickupReadyOrderObj;
    case 'DL':
      return constants.orders.readyOrderPopup.deliveryReadyOrderObj;
    default:
  }
};

export const getHeaderText = ({ pathname, guestName = '' }) => {
  if (!!guestName) return guestName; // If guestname is provided, return it as the header text
  let text = '';
  const currentPath = pathname.substr(pathname.lastIndexOf('/') + 1);
  switch (currentPath) {
    case restaurantRoutes.containers.orders:
      text = constants.body.ordersText;
      break;
    case restaurantRoutes.containers.history:
      text = constants.orderHistory.title;
      break;
    case restaurantRoutes.containers.menu:
      text = constants.menu.title;
      break;
    case restaurantRoutes.menu.menuItems:
      text = constants.menu.title;
      break;
    case restaurantRoutes.menu.customizations:
      text = constants.menu.title;
      break;
    case restaurantRoutes.containers.guests:
      text = constants.guests.title;
      break;
    case restaurantRoutes.containers.settings:
      text = constants.settings.title;
      break;
    case restaurantRoutes.containers.account:
      text = constants.account.title;
      break;
    case restaurantRoutes.containers.offers:
      text = constants.offers.title;
      break;
    case restaurantRoutes.offers.offerList:
      text = constants.offers.title;
      break;
    case restaurantRoutes.offers.archivedOffers:
      text = constants.archivedOffers.title;
      break;
    case restaurantRoutes.containers.upsell:
      text = restaurantRoutes.containers.upsell;
      break;
    case restaurantRoutes.containers.cashCard:
      text = cashCardResources.title;
      break;
    case restaurantRoutes.cashCard.list:
      text = cashCardResources.cardList.title;
      break;
    case restaurantRoutes.cashCard.transaction:
      text = cashCardResources.cashCardUsage.title;
      break;
    case restaurantRoutes.integrations.pos:
      text = integrationsResources.pos.posIntegration;
      break;
    case restaurantRoutes.pos.syncLogs:
      text = integrationsResources.pos.syncLogs.title;
      break;
    default:
      text = '';
  }
  return text;
};

/**
 * Extracts out specified notification sound path from notificationSounds array
 *
 * @param {String} orderNotificationSound The order notification sound code whose CDN path is to be fetched
 * @param {Array} notificationSounds The notification sounds array containing info about CDN paths of the sounds
 * @returns {String} notificationSoundPath Extracted CDN path of the given notification sound code
 */
export const getNotificationSoundPath = (orderNotificationSound, notificationSounds) => {
  const matchingNotificationSound = notificationSounds.find(
    (soundObj) => get(soundObj, 'code') === orderNotificationSound,
  );
  const notificationSoundPath = get(matchingNotificationSound, 'fullPath', '');

  return notificationSoundPath;
};

/**
 * Returns boolean which determines whether to play the audio in a loop.
 * @param {String} notificationPlaybackModeCode Code indicating whether the playback is configured to be single or continuous (looped)
 * @returns {Boolean} Indicates whether to loop the audio
 */
export const shouldNotificationSoundLoop = (notificationPlaybackModeCode) =>
  notificationPlaybackModeCode === notificationPlaybackModes.single ? false : true;

export const createExistingUserDropdownOptions = ({ fudrAdminData }) => {
  let dropdownOptions = [];
  const objKeys = keys(fudrAdminData);
  objKeys &&
    objKeys.map((option) => dropdownOptions.push({ key: option, text: option, value: option }));
  return dropdownOptions;
};

export const generateRestaurantMenuUrl = (scanCode, type = '') => {
  let url = publicUrl;
  const welcomeSubdomain = get(constants, 'settings.welcomeSubDomain', 'welcome');
  const customLinkSubdomain = get(constants, 'settings.menuLinkSettings.customLinkSubDomain', 'c');
  if (type === 'custom') {
    url = `${publicUrl}/${customLinkSubdomain}/${scanCode}`;
  } else {
    url = `${publicUrl}/${welcomeSubdomain}/${scanCode}`;
  }
  return url;
};

export const renderOfferBtnCTA = ({ mode }) => {
  let btnCTA = {
    'edit-offer': constants.offers.CTABtn.updateBtn,
    'view-offer': constants.offers.CTABtn.doneCTA,
    'create-offer': constants.offers.CTABtn.createBtn,
    'clone-offer': constants.offers.CTABtn.cloneBtn,
  };
  return btnCTA[mode];
};
export const renderOfferPopupTitle = ({ mode }) => {
  let title = {
    'view-offer': <span className="title">{constants.offers.offerDetails}</span>,
    'create-offer': <span className="title">{constants.offers.newOffer}</span>,
    'edit-offer': <span className="title">{constants.offers.editOffer}</span>,
    'clone-offer': <span className="title">{constants.offers.cloneOffer}</span>,
  };
  return title[mode];
};

export const renderOfferType = ({ type }) => {
  let offerType = {
    DD: constants.offers.DD,
    IO: constants.offers.IO,
    QD: constants.offers.QD,
  };
  return offerType[type];
};

/**
 * Gets the error title from the configuration on UI.
 * If not found, fallback to global error title.
 *
 * @param {Object} param Destructurable object
 * @param {Object} param.source Error source component's configuration object
 * @param {string} param.code Error code
 * @return {string} errorTitle Contextual error title
 */
export const getErrorTitle = ({ source, code }) => {
  let errorTitle = '';
  const configuredErrorTitle =
    source && source.errorInfo && source.errorInfo[code] && source.errorInfo[code].title
      ? source.errorInfo[code].title
      : '';
  const configGlobalErrorTitle =
    constants.globalErrorInfo &&
    constants.globalErrorInfo[code] &&
    constants.globalErrorInfo[code].title
      ? constants.globalErrorInfo[code].message
      : constants.globalErrorInfo.globalErrorTitle;
  errorTitle = !isEmpty(configuredErrorTitle) ? configuredErrorTitle : configGlobalErrorTitle;
  return errorTitle;
};

/**
 * Gets the error message from the configuration on UI.
 * If not found, fallback to API provided message and further fallback to global error message.
 *
 * @param {Object} param Destructurable object
 * @param {Object} param.source Error source component's configuration object
 * @param {string} param.code Error code
 * @param {string} [param.message] API provided error message / Custom fallback message
 * @return {string} errorMessage Contextual error message
 */
export const getErrorMessage = ({ source, code, message }) => {
  let errorMessage = '';
  const configErrorMessage =
    source && source.errorInfo && source.errorInfo[code] && source.errorInfo[code].message
      ? source.errorInfo[code].message
      : '';
  const configGlobalErrorMessage =
    constants.globalErrorInfo &&
    constants.globalErrorInfo[code] &&
    constants.globalErrorInfo[code].message
      ? constants.globalErrorInfo[code].message
      : '';
  errorMessage = !isEmpty(configErrorMessage) ? configErrorMessage : message;
  errorMessage = !isEmpty(errorMessage) ? errorMessage : configGlobalErrorMessage;
  return errorMessage;
};

/**
 * Gets error call-to-action text from the configuration on UI.
 * If not found, fallback to global CTA text.
 *
 * @param {Object} param Destructurable object
 * @param {Object} param.source Error source component's configuration object
 * @param {string} param.code Error code
 * @return {string} errorCTAText Contextual error CTA text
 */
export const getErrorCTAText = ({ source, code }) => {
  let errorCTAText = '';
  const configErrorCTAText =
    source && source.errorInfo && source.errorInfo[code] && source.errorInfo[code].CTAText
      ? source.errorInfo[code].CTAText
      : '';
  const configGlobalErrorCTAText =
    constants.globalErrorInfo &&
    constants.globalErrorInfo[code] &&
    constants.globalErrorInfo[code].CTAText
      ? constants.globalErrorInfo[code].CTAText
      : '';
  errorCTAText = !isEmpty(configErrorCTAText) ? configErrorCTAText : configGlobalErrorCTAText;
  return errorCTAText;
};

export const renderLocalitiesChecklist = (
  localities,
  handleLocalityCheckChange,
  selectedLocalityIds,
) => {
  let localitiesDOM = [];
  localitiesDOM =
    localities &&
    map(localities, ({ id, name }) => (
      <CheckBox
        key={id}
        label={startCase(name)}
        checked={findIndex(selectedLocalityIds, (value) => value === id) > -1}
        onChange={(event) => handleLocalityCheckChange(event, id)}
      />
    ));

  return localitiesDOM;
};

export const renderDeliveryChargeTypeTitle = (type) =>
  type && constants.settings.deliverySettings.deliveryChargeTypeTitle[type];

export const renderDeliveryChargesDOM = (charge) => {
  const deliverySettingsConfig = constants.settings.deliverySettings;
  switch (charge.ruleType) {
    case deliveryChargeRuleTypes.orderValue:
    case deliveryChargeRuleTypes.deliveryDistance:
      const rangeList = extractDeliveryChargeRange(charge);
      return (
        <div className="settings_row">
          <div className="settings_restaurant-information additional-charge-type">
            <span className="label">
              {constants.settings.additionalChargesSettings.deliveryChargesTitle}
            </span>
            <div className="delivery-charges_type info">
              {deliverySettingsConfig.deliveryChargeTypeTitle[charge.ruleType]}
            </div>
          </div>
          <div className="settings_restaurant-information">
            <div className="delivery-charges_table">
              <div className="delivery-charges_column">
                <div className="delivery-charges_column-heading">
                  {getDeliveryChargeLabel(charge.ruleType)}
                </div>
                {map(rangeList, (range) => (
                  <div key={range.id} className="delivery-charges_range-item info">
                    {range.range}
                  </div>
                ))}
              </div>
              <div className="delivery-charges_column">
                <div className="delivery-charges_column-heading">
                  {deliverySettingsConfig.deliveryChargeLabel}
                </div>
                {map(rangeList, (range) => (
                  <div key={range.id} className="delivery-charges_range-charge info">
                    {range.charge}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    case deliveryChargeRuleTypes.fixed:
      return (
        <div className="settings_restaurant-information">
          <span className="label">
            {constants.settings.additionalChargesSettings.deliveryChargesTitle}
          </span>
          <div className="delivery-charges_type info">
            {`${
              deliverySettingsConfig.deliveryChargeTypeTitle[charge.ruleType]
            } - ${formatDeliveryChargeValue(charge.fixedCharge)}`}
          </div>
        </div>
      );
    default:
      return <></>;
  }
};

export const extractDeliveryChargeRange = (charge) => {
  const rangeList = get(charge, 'restaurantDeliveryChargeRangeList', []);
  const ruleType = charge?.ruleType ?? '';
  return map(rangeList, (range, index, rangeList) => ({
    id: range.id,
    range: formatDeliveryChargeRange({ rangeList, index, ruleType }),
    charge: formatDeliveryChargeValue(range.charge),
  }));
};

export const formatDeliveryChargeRange = ({ rangeList, index, ruleType }) =>
  rangeList[index + 1]
    ? `${isEqual(ruleType, deliveryChargeRuleTypes.orderValue) ? `₹` : ''} ${
        rangeList[index].startRange
      } - ${rangeList[index + 1].startRange - 1}`
    : `${isEqual(ruleType, deliveryChargeRuleTypes.orderValue) ? `₹` : ''} ${
        rangeList[index].startRange - 1
      }+`;

export const formatDeliveryChargeValue = (charge) =>
  // Loosely compare charge with charge value provided in decimals
  // eslint-disable-next-line eqeqeq
  charge != 0 ? `₹ ${charge}` : constants.settings.deliverySettings.zeroChargeText;

/**
 * Extract list of restaurant timings as per the service type
 *
 * @param {array} weekDays Weekdays master data array
 * @param {array} workhours Collection of workhour objects
 * @param {string} type Service type
 * @return {array} Collection of workhour objects filtered by service type
 */
export const extractTimingsList = (weekDays = [], workhours, type = '') => {
  switch (type) {
    case serviceModes.dineIn:
    case serviceModes.selfPickup:
    case serviceModes.delivery:
    case serviceModes.driveThrough:
    case serviceModes.room:
      return filter(workhours, ['serviceType', type]);
    default:
      // Case for restaurant's general timings
      let restaurantGeneralTimings = filter(workhours, (workhour) => !has(workhour, 'serviceType'));
      if (restaurantGeneralTimings.length === 0) {
        restaurantGeneralTimings = weekDays.map((weekDay) => ({
          id: generateUUID(),
          day: get(weekDay, 'code', ''),
          startTime: fallbackTime,
          endTime: fallbackTime,
          isClosed: false,
        }));
      }

      return restaurantGeneralTimings;
  }
};

/**
 * Render DOM of restaurant timings as per the service type
 *
 * @param {array} weekDays Collection of weekDay objects representing all days of the week
 * @param {array} workhours Collection of workhour objects representing day-wise restaurant timings
 * @param {string} type Service type
 * @return {Array} Array containing workhour DOM nodes filtered by service type
 */
export const renderTimings = (weekDays, workhours, type = '') => {
  const timingsConfig = constants.settings.timings;
  const timings = [...extractTimingsList(weekDays, workhours, type)];
  // Compare function takes values being compared as parameters from sort() function and returns a comparator (integer) - negative implying first comes before, zero implying same and positive implying after, post sorting.
  const compareFunction = (firstValue, secondValue) => {
    const comparator =
      get(
        find(weekDays, (day) => get(day, 'code', '') === get(firstValue, 'day', '')),
        'ordinal',
        1,
      ) -
      get(
        find(weekDays, (day) => get(day, 'code', '') === get(secondValue, 'day', '')),
        'ordinal',
        1,
      );
    return comparator;
  };
  timings.sort(compareFunction);

  return map(timings, (timing, index) => {
    const matchingWeekDayObject = find(weekDays, ['code', get(timing, 'day', '')]); // Extract weekday object from master data corresponding to current timing's day key
    const matchingWeekDayLabel =
      matchingWeekDayObject && get(matchingWeekDayObject, 'fullTitle', '');
    const timingHoursText = get(timing, 'isClosed', false)
      ? timingsConfig.dayStatusClosed
      : `${formatTimeTo12H(get(timing, 'startTime', fallbackTime))} - ${formatTimeTo12H(
          get(timing, 'endTime', fallbackTime),
        )}`;
    return (
      <div className="timings_row" key={index}>
        <div className="timings_day">{matchingWeekDayLabel}</div>
        <div className="timings_hours">{timingHoursText}</div>
      </div>
    );
  });
};

/**
 * Gets timing day status text which would indicate if restaurant day is open or closed
 *
 * @param {object} timing Timing/Workhour object
 * @return {string} Timing day status text from configuration
 */
export const getRestaurantTimingDayStatus = (timing) => {
  const timingsPopupConfig = constants.settings.timings.popup;
  const isDayClosed = get(timing, 'isClosed', false);
  return isDayClosed ? timingsPopupConfig.dayStatus.closed : timingsPopupConfig.dayStatus.open;
};

export const formulateCustomLinkSearchRequestUrl = ({ size }) => `/customLinks/search?size=${size}`;

/**
 * Gets the title for Menu Item Details Popup
 * @param {object} [opType] Operation type - in regard to menu item
 * @returns {string} Menu Item Details Popup title
 */
export const getMenuItemDetailsPopupTitle = ({ opType }) => {
  const menuConfig = get(constants, 'menu', {});
  const menuItemDetailsConfig = get(constants, 'menuItemDetails', {});

  switch (opType) {
    case get(menuConfig, 'itemOps.add', 'ADD'):
      return get(menuItemDetailsConfig, 'addItemTitle', 'Add Item');
    case get(menuConfig, 'itemOps.edit', 'EDIT'):
      return get(menuItemDetailsConfig, 'editItemTitle', 'Edit Item');
    case get(customizationOps, 'addCustomization', 'ADD_CUSTOMIZATION'):
      return get(menuItemDetailsConfig, 'addCustomizationTitle', 'Add Customization');
    case get(customizationOps, 'editCustomization', 'EDIT_CUSTOMIZATION'):
      return get(menuItemDetailsConfig, 'editCustomizationTitle', 'Edit Customization');
    case get(mediaOps, 'addMedia', 'ADD_MEDIA'):
      return get(menuItemDetailsConfig, 'addMediaTitle', 'Add Photo');
    case get(mediaOps, 'editMedia', 'EDIT_MEDIA'):
      return get(menuItemDetailsConfig, 'editMediaTitle', 'Edit Photo');
    default:
      return menuItemDetailsPopupFallbackTitle; // Fallback popup title
  }
};

/**
 * Gets the CTA Button Text for Menu Item Details Popup
 * @param {object} [opType] Operation type - in regard to menu item
 * @returns {string} Menu Item Details Popup CTA Button Text
 */
export const getMenuItemDetailsPopupCTABtnText = ({ opType }) => {
  const menuConfig = get(constants, 'menu', {});
  const menuItemDetailsConfig = get(constants, 'menuItemDetails', {});

  switch (opType) {
    case get(menuConfig, 'itemOps.add'):
      return get(menuItemDetailsConfig, 'addCTA', 'ADD');
    case get(menuConfig, 'itemOps.edit'):
      return get(menuItemDetailsConfig, 'updateCTA', 'UPDATE');
    case get(customizationOps, 'addCustomization', 'ADD_CUSTOMIZATION'):
      return get(menuItemDetailsConfig, 'addCustomizationCTA', 'ADD CUSTOMIZATION');
    case get(customizationOps, 'editCustomization', 'EDIT_CUSTOMIZATION'):
      return get(menuItemDetailsConfig, 'editCustomizationCTA', 'UPDATE CUSTOMIZATION');
    case get(mediaOps, 'addMedia', 'ADD_MEDIA'):
      return get(menuItemDetailsConfig, 'addMediaCTA', 'ADD PHOTO');
    case get(mediaOps, 'editmedia', 'EDIT_MEDIA'):
      return get(menuItemDetailsConfig, 'editMediaCTA', 'UPDATE PHOTO');
    default:
      return submitFallbackText; // Fallback CTA text
  }
};

export const renderFoodTypeIcon = ({ foodType }) => {
  switch (foodType) {
    case 'VEGETARIAN':
      return <img src={vegetarianIcon} alt="Veg" />;
    case 'NON_VEGETARIAN':
      return <img src={nonVegetarianIcon} alt="Non-veg" />;
    case 'EGG':
      return <img src={eggetarianIcon} alt="Egg" />;
    default:
      return <img src={defaultFoodIcon} alt="Other" />;
  }
};

export const renderFoodTypeTitle = (foodTypes, type) => {
  const foodTypeObj = find(foodTypes, ['code', type]);
  return toTitleCase(get(foodTypeObj, 'title', 'Default').toLowerCase());
};

export const renderFoodTypeDropdownOptions = (foodTypes = []) => {
  const dropdownOptions = map(foodTypes, (type) => {
    const foodTypeIcon = renderFoodTypeIcon({ foodType: get(type, 'code') });
    return {
      key: get(type, 'code'),
      value: get(type, 'code'),
      text: toTitleCase(get(type, 'title', 'Default').toLowerCase()),
      image: { src: get(foodTypeIcon, 'props.src'), height: '14px' },
    };
  });
  return dropdownOptions;
};

export const renderTaxDropdownOptions = ({ taxRates }) => {
  const dropdownOptions = [];
  map(taxRates, (rate, index) => {
    dropdownOptions.push({
      key: rate?.id ?? index,
      value: rate?.id ?? '',
      text: `${rate?.taxRate ?? '0.0'}%`,
    });
  });
  return dropdownOptions;
};

export const renderItemCategoryDropdownOptions = ({ menuCategories }) => {
  const dropdownOptions = [];
  map(menuCategories, (category, index) => {
    dropdownOptions.push({
      key: get(category, 'id', index),
      value: get(category, 'id', ''),
      text: `${has(category, 'parentName') ? get(category, 'parentName').concat(' - ') : ''}${get(
        category,
        'name',
        '',
      )}`,
    });
  });
  return sortBy(dropdownOptions, ['text']);
};

export const getOfferStatusClass = (status) => {
  switch (status) {
    case 'ARCHIVED':
      return 'archived';
    case 'UPCOMING':
      return 'upcoming';
    case 'ACTIVE':
      return 'active';
    case 'PAUSED':
      return 'paused';
    default:
      return 'active';
  }
};

export const renderCategoryDetailPopupTitle = (opType) => {
  switch (opType) {
    case menuCategoryOps.addCategory:
      return constants.menu.categoryDetails.addCategoryTitle;
    case menuCategoryOps.editCategory:
      return constants.menu.categoryDetails.editCategoryTitle;
    default:
      return constants.menu.categoryDetails.fallBackTitle;
  }
};

export const renderCategoryDetailPopupCTABtnText = (opType) => {
  switch (opType) {
    case menuCategoryOps.addCategory:
      return constants.menu.categoryDetails.addCTA;
    case menuCategoryOps.editCategory:
      return constants.menu.categoryDetails.updateCTA;
    default:
      return submitFallbackText;
  }
};

export const renderCategoryDetailsByType = (menuCategoryType) => {
  const menuCategoryDetailsConfig = constants.menu.categoryDetails;
  switch (menuCategoryType) {
    case menuCategoryTypes.main:
      return {
        nameLabel: menuCategoryDetailsConfig.categoryNameLabel,
        categoryLabel: menuCategoryDetailsConfig.mainCategoryLabel,
        convertCTA: menuCategoryDetailsConfig.convertToSubCategoryCTA,
      };
    case menuCategoryTypes.sub:
      return {
        nameLabel: menuCategoryDetailsConfig.subCategoryNameLabel,
        categoryLabel: menuCategoryDetailsConfig.subCategoryLabel,
        convertCTA: menuCategoryDetailsConfig.convertToMainCategoryCTA,
      };
    default:
      return {};
  }
};

export const renderParentCategoryDropdownOptions = (mainCategories) => {
  const dropdownOptions = [];
  map(mainCategories, (category, index) => {
    dropdownOptions.push({
      key: get(category, 'id', index),
      value: get(category, 'id', ''),
      text: get(category, 'name', ''),
    });
  });

  return sortBy(dropdownOptions, ['text']);
};

export const renderPaymentStatus = ({ statusCode, paymentType }) => {
  let paymentStatus = '';
  if (isEqual(statusCode, orderStatusCode.FAILED)) {
    paymentStatus = (
      <Label classNames="order-paymentFailed" text={constants.payments.paymentFailed} />
    );
  } else {
    if (isEqual(paymentType, paymentTypes.PREPAYMENT)) {
      paymentStatus = (
        <Label classNames="order-paymentDone" text={constants.payments.paymentDone} />
      );
    } else {
      if (
        isEqual(paymentType, paymentTypes.POSTPAYMENT) &&
        isEqual(statusCode, orderStatusCode.SETTLED)
      ) {
        paymentStatus = (
          <Label classNames="order-paymentDone" text={constants.payments.paidToRestaurant} />
        );
      } else {
        paymentStatus = (
          <Label classNames="order-paymentPending" text={constants.payments.paymentPending} />
        );
      }
    }
  }
  return paymentStatus;
};

export const getRefundStatus = (status) => {
  switch (status) {
    case refundStatus.success:
      return orderTransactionDetails.refundDetails.refundStatus.processed;
    case refundStatus.initial:
      return orderTransactionDetails.refundDetails.refundStatus.initiated;
    case refundStatus.pending:
      return orderTransactionDetails.refundDetails.refundStatus.initiated;
    case refundStatus.failed:
      return orderTransactionDetails.refundDetails.refundStatus.failed;
    default:
      return;
  }
};

export const getPaymentModeLabel = (paymentType) => {
  switch (paymentType) {
    case paymentTypes.loyaltyPaymentTypes.CASHCARD:
      return (
        <span>
          <img src={paidByCashCardIcon} alt="cahscard" />
          {orderTransactionDetails.cashCard}
        </span>
      );
    case paymentTypes.PREPAYMENT:
      return (
        <span>
          <img src={fudrPaidIcon} alt="fudr-paid" />
          {orderTransactionDetails.fudrPaid}
        </span>
      );
    case paymentTypes.POSTPAYMENT:
      return (
        <span>
          <img src={payToRestaurantIcon} alt="payToRestaurant" />
          {orderTransactionDetails.payToRestaurant}
        </span>
      );
    default:
      return '';
  }
};

export const getPaymentStatus = ({
  orderStatus,
  paymentType,
  paymentStatus,
  transactionSubStatus,
}) => {
  switch (paymentType) {
    case paymentTypes.POSTPAYMENT:
      return (
        (orderStatus === orderStatusCode.SETTLED &&
          orderTransactionDetails.paymentDetails.paymentStatus.successful) ||
        orderTransactionDetails.paymentDetails.paymentStatus.pending
      );
    case paymentTypes.PREPAYMENT:
    case paymentTypes.loyaltyPaymentTypes.CASHCARD:
      let txnSubStatus = '';
      switch (transactionSubStatus) {
        case transactionSubStatusConfig.userDropped:
          txnSubStatus = orderTransactionDetails.userDroppedStatus;
          break;
        case transactionSubStatusConfig.invalid:
          txnSubStatus = orderTransactionDetails.invalidTxnStatus;
          break;
        default:
          txnSubStatus = transactionSubStatus;
          break;
      }
      if (txnSubStatus) {
        return `${paymentStatus} (${txnSubStatus})`;
      }
      return `${paymentStatus}` || '';
    default:
      return '';
  }
};

export const getRefundModeLabel = (refundMode) => {
  switch (refundMode) {
    case refundTypesConfig.loyaltyRefundTypes.CASHCARD:
      return orderTransactionDetails.refundDetails.refundMode.cashCard;
    case refundTypesConfig.prepaid:
      return orderTransactionDetails.refundDetails.refundMode.prepaid;

    default:
      return '';
  }
};

export const getPaymentModeTypeData = (paymentType, hasPointsPayment, netBalance) => {
  // check for the point transaction
  if (hasPointsPayment) {
    if (netBalance > 0) {
      // if netBalance > 0, then remaining payment mode will be prepaid or postpaid
      switch (paymentType) {
        case paymentTypes.PREPAYMENT:
          return {
            iconSrc: cashCardPlusFudrPaid,
            tooltipContent: `${constants.tooltips.cashCard} + ${constants.tooltips.fudrPaid}`,
          };
        case paymentTypes.POSTPAYMENT:
          return {
            iconSrc: cashCardPlusPayToRestaurantIcon,
            tooltipContent: `${constants.tooltips.cashCard} + ${constants.tooltips.payToRestaurant}`,
          };
        default:
          return {
            iconSrc: '',
            tooltipContent: '',
          };
      }
    }
    // mode will be cash card
    return {
      iconSrc: paidByCashCardIcon,
      tooltipContent: constants.tooltips.cashCard,
    };
  }
  // check for pre payemnt or post payment
  switch (paymentType) {
    case paymentTypes.PREPAYMENT:
      return {
        iconSrc: fudrPaidIcon,
        tooltipContent: constants.tooltips.fudrPaid,
      };
    case paymentTypes.POSTPAYMENT:
      return {
        iconSrc: payToRestaurantIcon,
        tooltipContent: constants.tooltips.payToRestaurant,
      };
    default:
      return {
        iconSrc: '',
        tooltipContent: '',
      };
  }
};

/**
 * Returns formatted charge as per the unit provided
 *
 * @param {Number} charge Charge value
 * @param {String} unit Units like absolute, percent, etc.
 * @returns {*} Formatted charge
 */
export const getFormattedChargeWithUnits = (charge, unit) => {
  switch (unit) {
    case 'ABS':
      return `₹ ${charge}`;
    case 'PER':
      return `${charge}%`;
    default:
      return charge;
  }
};

/**
 * Get service type titles in a comma-separated text format
 *
 * @param {Array} restaurantOrderModes restaurant specific order mode
 * @param {Array} masterServiceTypes Master service types data
 * @param {Array} specificServiceTypeArr Specific service type codes to format
 * @returns {String} Comma separated service type titles
 */
export const getFormattedCommaSeparatedServiceTypes = (
  restaurantOrderModes,
  masterServiceTypes,
  specificServiceTypeArr,
) => {
  let applicableServiceTypes = getApplicableServiceTypes(restaurantOrderModes, masterServiceTypes);
  applicableServiceTypes = applicableServiceTypes.filter((sType) =>
    specificServiceTypeArr.includes(sType.code),
  );
  return (
    applicableServiceTypes &&
    applicableServiceTypes.length > 0 &&
    applicableServiceTypes
      .map(
        (serviceType) =>
          `${
            !isEmpty(get(serviceType, 'customName', ''))
              ? toTitleCase(get(serviceType, 'customName', ''))
              : get(serviceType, 'title', '')
          } Orders`,
      )
      .join(', ')
  );
};

/**
 * Returns the toast message to be display when a timing setting specific to a service type is updated
 *
 * @param {String} serviceType Type of restaurant service
 * @returns {String} Toast message from configuration
 */
export const getSpecificTimingsSettingsUpdateToastMessage = (serviceType) => {
  const timingsSettingsConfig = constants.settings.timings;
  switch (serviceType) {
    case serviceModes.dineIn:
      return timingsSettingsConfig.dineInTimingsUpdateToastMessage;
    case serviceModes.selfPickup:
      return timingsSettingsConfig.selfPickupTimingsUpdateToastMessage;
    case serviceModes.delivery:
      return timingsSettingsConfig.deliveryTimingsUpdateToastMessage;
    case serviceModes.driveThrough:
      return timingsSettingsConfig.driveThroughTimingsUpdateToastMessage;
    case serviceModes.room:
      return timingsSettingsConfig.roomTimingsUpdateToastMessage;
    default:
      return timingsSettingsConfig.restaurantTimingsUpdateToastMessage;
  }
};

export const getApplicableServiceTypes = (restaurantServiceTypesArr, serviceTypesArr) => {
  // Filter out restaurant service types that are not stopped.
  const filteredRestaurantServiceTypesArr = restaurantServiceTypesArr.filter(
    (restaurantServiceType) => !get(restaurantServiceType, 'isStopped'),
  );
  let arr = filteredRestaurantServiceTypesArr.map((restaurantServiceType) => {
    return {
      ...serviceTypesArr.find((serviceType) => serviceType.code === restaurantServiceType.code),
      ...restaurantServiceType,
    };
  });
  return arr;
};

export const renderEstimatedTimeOfDelivery = ({
  orderPunchTime,
  ePrepTime,
  ePickupTime,
  defaultPreparationTime,
}) => {
  let etd = '';
  if (isEmpty(ePickupTime || ePickupTime === 0)) {
    if (isNumber(ePrepTime)) {
      etd = formatTime(orderPunchTime, ePrepTime);
    } else {
      etd = formatTime(orderPunchTime, defaultPreparationTime);
    }
  } else {
    etd = formatTime(orderPunchTime, max([ePrepTime, ePickupTime]));
  }
  return etd;
};

export const renderRestaurantOrderModesChecklist = ({
  masterServiceTypes,
  restaurantServiceTypes,
  additionalChargeServiceTypes,
  onCheckChange,
}) => {
  const orderModes = getApplicableServiceTypes(restaurantServiceTypes, masterServiceTypes);
  return orderModes.map((orderMode, index) => {
    const orderModeExists = additionalChargeServiceTypes.some(
      (additionalChargeServiceType) =>
        get(additionalChargeServiceType, 'serviceType', '') === get(orderMode, 'code', ''),
    );
    return (
      <CheckBox
        key={get(orderMode, 'code', index)}
        label={`${
          !isEmpty(get(orderMode, 'customName', ''))
            ? toTitleCase(get(orderMode, 'customName', ''))
            : get(orderMode, 'title', '')
        } Orders`}
        checked={orderModeExists}
        onChange={() => onCheckChange(get(orderMode, 'code', ''))}
      />
    );
  });
};

export const renderChargeUnitsDropdownOptions = (units = []) =>
  map(units, (unit) => ({
    key: get(unit, 'code', ''),
    value: get(unit, 'code', ''),
    text: get(unit, 'title', ''),
  }));

export const getMediaPlaybackIcon = (mediaOp, onClick) => {
  switch (mediaOp) {
    case browserMediaOps.play:
      return <img src={playIcon} alt="play" onClick={(event) => onClick(event, mediaOp)} />;
    case browserMediaOps.pause:
      return <img src={pauseIcon} alt="pause" onClick={(event) => onClick(event, mediaOp)} />;
    case browserMediaOps.stop:
      return <img src={stopIcon} alt="stop" onClick={(event) => onClick(event, mediaOp)} />;
    default:
      return <img src={playIcon} alt="play" onClick={(event) => onClick(event, mediaOp)} />;
  }
};

export const getNotificationEmailSettingsUIElementText = (notificationEmailType, UIElementType) => {
  const notificationEmailSettingsConfig =
    constants.settings.communicationSettings.notificationEmailSettings;
  const isSubtitle = UIElementType === UIElements.popupSubTitle;
  const notificationEmailSettingsUIElementConfig = isSubtitle
    ? notificationEmailSettingsConfig.popup.subTitle
    : notificationEmailSettingsConfig.updateToastMessage;
  switch (notificationEmailType) {
    case emailTypes.newOrder:
      return notificationEmailSettingsUIElementConfig.newOrder;
    case emailTypes.orderSummary:
      return notificationEmailSettingsUIElementConfig.orderSummary;
    case emailTypes.offer:
      return notificationEmailSettingsUIElementConfig.offer;
    case emailTypes.guestList:
      return notificationEmailSettingsUIElementConfig.guestList;
    case emailTypes.cancelOrder:
      return notificationEmailSettingsUIElementConfig.cancelOrder;
    case emailTypes.customerSupport:
      return notificationEmailSettingsUIElementConfig.customerSupport;
    default:
      return isSubtitle ? '' : constants.settings.defaultUpdateToastMessage;
  }
};

export const renderErrorCTABtn = (pageUrl) => {
  switch (pageUrl) {
    case `${CONTEXT}/${restaurantRoutes.restaurant}`:
      return errorPageConfig.dashboardDefaultCTABtn;
    default:
      return errorPageConfig.defaultCTABtn;
  }
};

export const checkForShowOrderSummaryActionBtn = (statusCode) => {
  const showOrderSummaryActionBtn = {
    [orderStatusCode.UNATTENDED]: false,
    [orderStatusCode.SETTLED]: false,
    [orderStatusCode.FAILED]: false,
    [orderStatusCode.CANCELLED]: false,
    [orderStatusCode.READY]: true,
    [orderStatusCode.CONFIRMED]: true,
  };
  return showOrderSummaryActionBtn[statusCode];
};

export const renderItemOpSymbol = (itemOp) => {
  const itemOpSymbols = {
    [itemOps.add]: <img src={plusIcon} alt="+" />,
    [itemOps.edit]: <img src={whiteEditIcon} alt="" />,
  };

  return itemOpSymbols[itemOp];
};

export const getFeatureStatus = (featuresStatusArr = [], type = '') => {
  let featurePausedState = {
    isPaused: true,
  };
  const status = get(
    featuresStatusArr.find((featureStatus) => featureStatus.code === type),
    'status',
    '',
  );
  switch (status) {
    case featuresStatus.active:
      return (featurePausedState = { isPaused: false });
    case featuresStatus.paused:
      return (featurePausedState = { isPaused: true });
    case featuresStatus.notAvailable:
      return (featurePausedState = {});
    default:
      return featurePausedState;
  }
};

export const createStateCityDropdownOptions = (optionData) => {
  let dropdownOptions = [];
  optionData?.length > 0 &&
    optionData.map((data) =>
      dropdownOptions.push({
        key: data?.id ?? '',
        value: data?.id ?? '',
        text: toTitleCase(data?.name ?? ''),
      }),
    );
  return dropdownOptions;
};

export const getDeliveryChargeLabel = (deliveryType) =>
  ({
    [deliveryChargeRuleTypes.deliveryDistance]:
      constants.settings.deliverySettings.deliveryChargesPopup.deliveryDistanceLabel,
    [deliveryChargeRuleTypes.orderValue]:
      constants.settings.deliverySettings.deliveryChargesPopup.orderRangeLabel,
  }[deliveryType]);

export const renderBrandTemplateStatus = (status) => {
  let brandStatus = status ?? '--';
  switch (status) {
    case 'SUGGEST':
      return 'Suggested';
    case 'LIVE':
      return 'Live';
    default:
      return brandStatus;
  }
};

export const createBrandIdDropdownOptions = (optionData) => {
  let dropdownOptions = [];
  optionData?.length > 0 &&
    optionData.map((data) =>
      dropdownOptions.push({
        key: data ?? '',
        value: data ?? '',
        text: data ?? '',
      }),
    );
  return dropdownOptions;
};
